import './radionomy.player.css'
interface LegacyPlayer {
  parameters: LegacyParameters;
}

interface LegacyParameters {
  autoplay: string | null;
  color1: string | null;
  color2: string | null;
  src: string | null;
  type: string | null;
  url: string | null;
  version: string | null;
  volume: string | null;
}

interface Stream {
  $id: string;
  ContactId: bigint;
  LogoPath: string;
  MountPoint: string;
  RadioUid: string;
  RadioWebsite: string;
  StreamUrl: string;
  Title: string;
}

type PlayerDomParts = {
  actions: HTMLElement | null;
  artist: HTMLAnchorElement | null;
  buttonPause: HTMLButtonElement | null;
  buttonPlay: HTMLButtonElement | null;
  cover: HTMLElement | null;
  error: HTMLElement | null;
  loader: HTMLElement | null;
  song: HTMLAnchorElement | null;
  title: HTMLAnchorElement | null;
  volume: HTMLInputElement | null;
}

type PlayerOptions = {
  autoplay: boolean;
  colorBackground: string | null;
  colorForeground: string | null;
  layout: "horizontal" | "mobile" | null;
  volume: Number | null;
}

type PlayerSong = {
  Artist: string | null;
  Callback: Number | null;
  Duration: Number | null;
  IsAdvert: boolean;
  Title: string | null;
  TrackCover: string | null;
}

const defaultPlayerOptions = {
  autoplay: false,
  colorBackground: '#24272d',
  colorForeground: '#0971e1',
  layout: null,
  volume: 100,
}

class Player {
  currentSong: PlayerSong | null = null;
  domParts: PlayerDomParts;
  element: HTMLElement;
  error: string | null = null;
  options: PlayerOptions = defaultPlayerOptions;
  stream: Stream;
  sound?: HTMLAudioElement;

  constructor(stream: Stream, element: HTMLElement, options?: JSON) {
    this.stream = stream
    this.element = element
    this.options = {
      ...defaultPlayerOptions,
      ...(options || {})
    }

    this.#setup()

    this.domParts = {
      actions: this.element.querySelector('[data-player="actions"]'),
      artist: this.element.querySelector('[data-player="artist"]'),
      cover: this.element.querySelector('[data-player="cover"]'),
      error: this.element.querySelector('[data-player="error"]'),
      loader: this.element.querySelector('[data-player="loader"]'),
      buttonPause: this.element.querySelector('[data-player="pause"]'),
      buttonPlay: this.element.querySelector('[data-player="play"]'),
      song: this.element.querySelector('[data-player="song"]'),
      title: this.element.querySelector('[data-player="title"]'),
      volume: this.element.querySelector('[data-player="volume"]'),
    }

    if (this.domParts.title) {
      this.domParts.title.textContent = this.stream.Title
      this.domParts.title.href = this.stream.RadioWebsite
    }

    this.domParts.artist && (this.domParts.artist.href = this.stream.RadioWebsite)
    this.domParts.song && (this.domParts.song.href = this.stream.RadioWebsite)
    this.domParts.volume && this.options.volume && (this.domParts.volume.value = (Number(this.options.volume) / 100).toString())

    this.#listen()

    this.syncMetadata()
  }

  #setup = () => {
    this.#setupHTML()
    this.#setupSound()
  }

  // Bind the Player HTML to the element
  #setupHTML = () => {
    this.element.classList.add('radionomy-player')
    if (this.options.layout) {
      this.element.classList.add(`layout--${this.options.layout}`)
    }
    const themeStyles = {
      '--color-background': this.options.colorBackground,
      '--color-foreground': this.options.colorForeground,
      '--cover-url': `url(${this.stream.LogoPath})`,
    }
    this.element.setAttribute('style', Object.entries(themeStyles).map(([key, value]) => `${key}: ${value}`).join('; '))
    this.element.innerHTML = `
      <div class="player--header">
          <a class="player--title" data-player="title" target="_blank"></a>
          <div class="player--actions" data-player="actions">
            <button class="player--action hidden" type="button" data-player="actions-share" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
              </svg>
            </button>
            <button class="player--action hidden" type="button" data-player="actions-external" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </button>
            <button class="player--action hidden" type="button" data-player="actions-like" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
            <button class="player--action hidden" type="button" data-player="actions-unlike" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd" />
              </svg>
            </button>
            <a href="https://shoutcast.com" class="player--action hidden mobile:inline-flex">
              <svg viewBox="0 0 32 41"><polygon fill="#18b9e7" points="20.3252747 20.4649573 20.3252747 0.245299145 0.0351648352 20.4649573"></polygon><polygon fill="#119bcc" points="11.7098901 20.4649573 11.7098901 40.6846154 32 20.4649573"></polygon><polygon fill="#2f6a8a" points="20.2549451 20.4649573 11.7098901 20.4649573 11.7098901 24.7401709"></polygon></svg>
            </a>
          </div>
      </div>
      <div class="player--cover" data-player="cover">
        <div class="player--controls">
            <button class="player--button${this.options.autoplay ? ' hidden' : ''}" data-player="play" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
              </svg>
            </button>
            <button class="player--button hidden" data-player="pause" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clip-rule="evenodd" />
              </svg>
            </button>
            <button class="player--button${this.options.autoplay ? '' : ' hidden'}" data-player="loader" type="button">
              <svg viewBox="0 0 32 41"><polygon fill="#18b9e7" points="20.3252747 20.4649573 20.3252747 0.245299145 0.0351648352 20.4649573"></polygon><polygon fill="#119bcc" points="11.7098901 20.4649573 11.7098901 40.6846154 32 20.4649573"></polygon><polygon fill="#2f6a8a" points="20.2549451 20.4649573 11.7098901 20.4649573 11.7098901 24.7401709"></polygon></svg>
            </button>
            <div class="player--error hidden" data-player="error"></div>
        </div>
        <div class="player--volume">
          <button class="player--button" data-player="volume-indicator" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clip-rule="evenodd" />
            </svg>
          </button>
          <input type="range" step="0.01" value="1" min=".15" max="1" data-player="volume" />
        </div>
      </div>
      <div class="player--meta">
        <div class="player--artist truncate">
          <a href="" target="_blank" data-player="artist" class="truncate"><span class="nowrap"></span></a>
        </div>
        <div class="player--song truncate">
          <a href="" target="_blank" data-player="song" class="truncate"><span class="nowrap"></span></a>
        </div>
      </div>
      <a class="player--logo mobile:hidden" href="https://shoutcast.com" target="_blank">
        <svg viewBox="0 0 32 41"><polygon fill="#18b9e7" points="20.3252747 20.4649573 20.3252747 0.245299145 0.0351648352 20.4649573"></polygon><polygon fill="#119bcc" points="11.7098901 20.4649573 11.7098901 40.6846154 32 20.4649573"></polygon><polygon fill="#2f6a8a" points="20.2549451 20.4649573 11.7098901 20.4649573 11.7098901 24.7401709"></polygon></svg>
        <span>shoutcast</span>
      </a>
    `
  }

  // Bind Stream to Audio Player
  #setupSound = () => {
    const protocol = ['https:', 'http:'].includes(window.location.protocol) ? '//' : 'https://'
    this.sound = new Audio(`${protocol}${this.stream.StreamUrl}`)

    this.sound.addEventListener('error', event => {
      let target = <HTMLAudioElement>event.target

      switch (target.error?.code) {
        case target.error?.MEDIA_ERR_ABORTED:
          this.setError('You aborted the video playback.')
          break;
        case target.error?.MEDIA_ERR_NETWORK:
          this.setError('Please verify your internet connection.')
          break;
        case target.error?.MEDIA_ERR_DECODE:
          this.setError('The stream seems corrupted or it used features your browser did not support.')
          break;
        case target.error?.MEDIA_ERR_SRC_NOT_SUPPORTED:
          this.setError('The stream could not be loaded, either because the server or network failed or because the format is not supported.')
          break;
        default:
          this.setError('An unknown error occured.')
      }

      this.sound = undefined
    })

    if (this.options.autoplay) {
      this.loaderStart()
      this.sound.addEventListener('canplay', _ => {
        this.play()
      })
    }

    this.sound.addEventListener('playing', _ => {
      this.domParts.buttonPause?.classList.remove('hidden')
      this.domParts.buttonPlay?.classList.add('hidden')
      this.loaderStop()
    })

    this.sound.addEventListener('pause', _ => {
      this.domParts.buttonPause?.classList.add('hidden')
      this.domParts.buttonPlay?.classList.remove('hidden')
      this.loaderStop()
    })
  }

  #listen = () => {
    this.domParts.buttonPlay && (this.domParts.buttonPlay.onclick = () => {
      this.play()
    })
    this.domParts.buttonPause && (this.domParts.buttonPause.onclick = () => {
      this.pause()
    })
    this.domParts.volume && (this.domParts.volume.addEventListener('change', e => {
      this.sound!.volume = parseFloat((<HTMLInputElement>e.target)!.value)
    }))
  }

  loaderStart = () => {
    this.domParts?.loader?.classList.remove('hidden')
  }

  loaderStop = () => {
    this.domParts.loader?.classList.add('hidden')
  }

  pause = () => {
    this.resetError()
    if (this.sound && this.sound.played) {
      this.sound.pause()
    }
  }

  play = () => {
    this.resetError()
    this.loaderStart()

    if (this.sound === undefined) {
      this.#setupSound()
      return
    }

    if (this.sound && this.sound.paused) {
      this.sound
        .play()
        .catch(e => {
          switch (e.name) {
            case 'NotAllowedError':
              // Do not show this error
              // this.setError('The player could not start because you denied permissions.')
              break;
          }

          this.domParts.buttonPause?.classList.add('hidden')
          this.domParts.buttonPlay?.classList.remove('hidden')
        })
    }

    this.loaderStop();
  }

  setError = (error: string) => {
    this.error = error

    if (this.domParts.error) {
      this.domParts.error.innerText = this.error
      this.domParts.error.classList.remove('hidden')
    }
  }

  resetError = () => {
    this.error = null
    this.domParts.error?.classList.add('hidden')
  }

  syncMetadata = () => {
    fetch(`https://ws.shoutcast.com/api/metadata/playing?radioUid=${this.stream.RadioUid}`)
      .then(response => {
        if (response.status !== 200) {
          throw Error('Error ' + response.status)
        }

        return response.json()
      })
      .then(data => {
        this.domParts.cover && this.domParts.cover.setAttribute('style', `--cover-url: url(${data.CoverUrl});`)
        this.domParts.artist && (this.domParts.artist.innerHTML = `<span>${data.Artist}</span>`)
        this.domParts.song && (this.domParts.song.innerHTML = `<span>${data.Title}</span>`)

        setTimeout(this.syncMetadata, data.CallbackDelay)
      })
      .catch(_ => {
        setTimeout(this.syncMetadata, 5000)
      })
  }

}

(<any>window).shoutcast = (domElement: HTMLElement, stream: Stream, options?: JSON) : Player => {
  const player = new Player(stream, domElement, options)

  return player
}


function InitDetachedPlayerFunc() {
  let win = (<any>window)
	var ref = win.document.getElementsByClassName('radionomy-player')[0];
	if (!ref) {
		return;
	}

	var rpo = win["RadionomyPlayerObject"];
	if (!rpo) {
		throw Error('RadionomyPlayerObject is not defined');
	}

	var cObjectName = rpo;
	if (typeof rpo === 'object') {
		cObjectName = rpo[0];
		rpo.splice(0, 1);
	}

  let radionomyPlayer : LegacyPlayer = win[cObjectName]
	var p : LegacyParameters = radionomyPlayer.parameters;

	if (!p.url || p.url.length == 0) {
		throw Error('parameter \'url\' is not defined');
	}

  fetch(`https://ws.shoutcast.com/api/player/radio-detail/${p.url}`)
    .then(response => {
      if (response.status !== 200) {
        throw Error('Error ' + response.status)
      }

      return response.json()
    })
    .then(data => {
      if (!data.DataItem) {
        throw Error(`No Stream found for ${p.url}`)
      }

      let stream : Stream = data.DataItem

      win.shoutcast(ref, stream, {
        autoplay: p.autoplay == '1',
        colorBackground: p.color1,
        colorForeground: p.color2,
        layout: p.type,
        volume: p.volume ? Number(p.volume) : 100
      })
    })
};

(function _InitDetachedPlayer() {
	if (/interactive|loaded|complete/.test(document.readyState)) {
		InitDetachedPlayerFunc();
	}
	else {
		window.onload = InitDetachedPlayerFunc;
	}
})();
